import { ColumnDef } from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";
import { Pagination, Table } from "@jhool-io/fe-components";
import { formatDate } from "../../../../../utils/helpers";
import { LISTS_DEFAULT_LIMIT } from "../../../../../utils/constants";
import ListState from "../../../../../components/ListState/ListState";
import Loader from "../../../../../shared-ui/Loader/Loader";
import { ICollectionsNoteActivity } from "../../../types/clients.types";
import { getCollectionsNoteUserName } from "../../../helpers/clients.helpers";
import { useFetchCollectionsNoteActivities } from "../../../hooks/clients.queries";

interface CollectionsNoteActivityProps {
    clientId: string;
    collectionsNoteId?: string;
}

const columns: ColumnDef<ICollectionsNoteActivity>[] = [
    {
        accessorKey: "date_created",
        header: "Date",
        cell: ({ row }) => formatDate(row.original.date_created),
    },
    {
        accessorKey: "collections_note_activity_id",
        header: "Time",
        cell: ({ row }) =>
            formatDate(row.original.date_created, false, "h:mma"),
    },
    {
        accessorKey: "author",
        header: "Author",
        cell: ({ row }) => (
            <span className="capitalize">
                {getCollectionsNoteUserName(row.original.author)}
            </span>
        ),
    },
    {
        accessorKey: "description",
        header: "Description",
    },
];

export default function CollectionsNoteActivity({
    clientId,
    collectionsNoteId,
}: CollectionsNoteActivityProps) {
    const [searchParams, setSearchParams] = useSearchParams();

    const pageFilter = Number(searchParams.get("activity_page")) || 1;
    const limitFilter =
        Number(searchParams.get("activity_limit")) || LISTS_DEFAULT_LIMIT;

    const { data, error, isLoading, isSuccess } =
        useFetchCollectionsNoteActivities(
            clientId,
            {
                collections_note_id: collectionsNoteId,
                page: pageFilter,
                limit: limitFilter,
            },
            Boolean(clientId)
        );

    const handlePageChange = (page: number) => {
        searchParams.set("activity_page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("activity_limit", String(limit));
        setSearchParams(searchParams);
    };

    return (
        <>
            {isLoading ? (
                <Loader dataTestId="collections-note-activity-loader" />
            ) : null}
            {error ? (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error.response?.data.message ||
                        "Cannot display collections note activity at this time please try again later"
                    }
                />
            ) : null}
            {data && isSuccess && data.data.length === 0 ? (
                <ListState
                    isError={false}
                    stateHelperText={
                        collectionsNoteId
                            ? "Changes to this note will appear here"
                            : "Changes to collections notes will appear here"
                    }
                    emptyMessage="No activity yet"
                />
            ) : null}
            {data && isSuccess && data.data.length > 0 ? (
                <Table
                    data={data.data}
                    columns={columns}
                    hasPagination={data.total_count > limitFilter}
                    pagination={
                        <Pagination
                            totalCount={data.total_count}
                            totalPages={data.total_pages}
                            currentPage={pageFilter}
                            onPageChange={handlePageChange}
                            onLimitChange={handleLimitChange}
                            limit={limitFilter}
                        />
                    }
                />
            ) : null}
        </>
    );
}
